import type {
  AntakshariData,
  TwoKeysData,
  JugnuData,
  PlanTripData,
  RankItData,
  StoryCardsData,
  WeWouldData,
  BucketData,
  MemoryLaneData,
  NPATData,
  SameWaveData,
  Beat,
  BeatType,
  VenueChoice,
  VenueId,
  VenueVote,
  DateView,
  Highlights,
  CharadesData,
  DoodleData,
  GameView,
  GuessAnswerData,
  HotTakesData,
  IcebreakerPrefs,
  Me,
  Message,
  SamePageData,
  TriviaData,
  TwoTruthsData,
} from './types';

/** SPEC_V3 §0 defaults: calm unless the user opts in. v6 (SPEC_V6 §2): the quiet-moment helper is on by default. */
export const DEFAULT_ICEBREAKER_PREFS: Required<IcebreakerPrefs> = { opener: 'chip', warmup: false, quiet_nudges: true, reactions: true, game_invites: true, spice_offers: true };

export function normalizePrefs(p: Partial<IcebreakerPrefs> | null | undefined): IcebreakerPrefs {
  const o = p ?? {};
  return {
    opener: o.opener === 'card' || o.opener === 'off' || o.opener === 'chip' ? o.opener : DEFAULT_ICEBREAKER_PREFS.opener,
    warmup: typeof o.warmup === 'boolean' ? o.warmup : DEFAULT_ICEBREAKER_PREFS.warmup,
    quiet_nudges: typeof o.quiet_nudges === 'boolean' ? o.quiet_nudges : DEFAULT_ICEBREAKER_PREFS.quiet_nudges,
    reactions: typeof o.reactions === 'boolean' ? o.reactions : DEFAULT_ICEBREAKER_PREFS.reactions,
    game_invites: typeof o.game_invites === 'boolean' ? o.game_invites : DEFAULT_ICEBREAKER_PREFS.game_invites,
    spice_offers: typeof o.spice_offers === 'boolean' ? o.spice_offers : DEFAULT_ICEBREAKER_PREFS.spice_offers,
  };
}

const BEAT_TYPES: BeatType[] = ['question', 'lightning', 'story', 'game', 'challenge', 'reveal', 'spice_gate', 'ritual', 'recall', 'capsule'];
const num = (x: unknown, d = 0) => (typeof x === 'number' && Number.isFinite(x) ? x : d);
const strs = (x: unknown): string[] => (Array.isArray(x) ? x.filter((v): v is string => typeof v === 'string') : []);
const titles = (x: unknown): { title: string }[] =>
  Array.isArray(x) ? x.flatMap((v) => (v && typeof v === 'object' && typeof (v as { title?: unknown }).title === 'string' ? [{ title: (v as { title: string }).title }] : typeof v === 'string' ? [{ title: v }] : [])) : [];

export function normalizeHighlights(h: Partial<Highlights> | null | undefined): Highlights | null {
  if (!h || typeof h !== 'object') return null;
  return {
    minutes: num(h.minutes),
    beats: num(h.beats),
    in_sync_pct: typeof h.in_sync_pct === 'number' ? h.in_sync_pct : null,
    laughs: num(h.laughs),
    loved: titles(h.loved),
    shared: strs(h.shared),
  };
}

function normalizeBeat(b: Partial<Beat> | null | undefined): Beat | null {
  if (!b || typeof b !== 'object' || !b.id) return null;
  const type = BEAT_TYPES.includes(b.type as BeatType) ? (b.type as BeatType) : 'question';
  return {
    id: String(b.id),
    type,
    act: b.act ?? '',
    title: b.title ?? '',
    body: b.body ?? '',
    for: b.for || 'both',
    min_s: num(b.min_s),
    soft_max_s: num(b.soft_max_s, type === 'story' ? 90 : 0),
    depth: b.depth ?? 'light',
    options: Array.isArray(b.options) ? b.options.map((o) => ({ a: o?.a ?? '', b: o?.b ?? '' })) : undefined,
    my_picks: Array.isArray(b.my_picks) ? b.my_picks.map((p) => (p === 'a' || p === 'b' ? p : null)) : undefined,
    their_picks: Array.isArray(b.their_picks) ? b.their_picks.map((p) => (p === 'a' || p === 'b' ? p : null)) : undefined,
    solo_next: b.solo_next === true,
    reveal: b.reveal && typeof b.reveal === 'object' ? b.reveal : null,
    game: b.game && b.game.game_id ? b.game : null,
    follow_ups: strs(b.follow_ups),
    hook: typeof b.hook === 'string' && b.hook ? b.hook : null,
    source: b.source === 'ai' ? 'ai' : 'library',
    capsule: Array.isArray(b.capsule)
      ? b.capsule.filter((l): l is { from: string; text: string } => !!l && typeof l.from === 'string' && typeof l.text === 'string').map((l) => ({ from: l.from, text: l.text }))
      : null,
    recall:
      b.recall && typeof b.recall === 'object'
        ? { chapter: num(b.recall.chapter), shared: strs(b.recall.shared), loved: titles(b.recall.loved), last_venue: isVenue(b.recall.last_venue) ? b.recall.last_venue : null }
        : null,
  };
}

/** v9 K2 venue ids (unknown ids from a newer server read as no venue). */
export const VENUE_IDS: VenueId[] = ['chai_tapri', 'rooftop_night', 'beach_sunset', 'bookstore_cafe', 'stargazing', 'candlelight'];
export const isVenue = (x: unknown): x is VenueId => typeof x === 'string' && (VENUE_IDS as string[]).includes(x);
/** v14: a venue or "No theme". */
export const isVenueChoice = (x: unknown): x is VenueChoice => x === 'none' || isVenue(x);

function normalizeVenueVote(v: Partial<VenueVote> | null | undefined): VenueVote | null {
  if (!v || typeof v !== 'object') return null;
  return { open: !!v.open, mine: isVenueChoice(v.mine) ? v.mine : null, theirs: isVenueChoice(v.theirs) ? v.theirs : null, flipped: !!v.flipped, winner: typeof v.winner === 'string' ? v.winner : null };
}

/** v6 DateView (SPEC_V6 §7): tolerate missing optional fields and Go nil maps/slices. */
export function normalizeDate(v: DateView | null | undefined): DateView | null {
  if (!v || typeof v !== 'object' || !v.id) return null;
  const sp = v.spice ?? { offered: false, my_answer: null, both_yes: false };
  return {
    ...v,
    length_min: v.length_min === 60 ? 60 : 30,
    status: v.status === 'paused' || v.status === 'ended' ? v.status : 'active',
    elapsed_ms: num(v.elapsed_ms),
    acts: (v.acts ?? []).map((a) => ({ id: a.id, name: a.name ?? '', state: a.state === 'done' || a.state === 'current' ? a.state : 'next' })),
    beat: normalizeBeat(v.beat),
    ready: v.ready ?? {},
    can_suggest_next: !!v.can_suggest_next,
    paused_by: v.paused_by ?? null,
    spice: { offered: !!sp.offered, my_answer: typeof sp.my_answer === 'boolean' ? sp.my_answer : null, both_yes: !!sp.both_yes },
    highlights: normalizeHighlights(v.highlights),
    venue: isVenue(v.venue) ? v.venue : null,
    venue_vote: normalizeVenueVote(v.venue_vote),
    chapter:
      v.chapter && typeof v.chapter === 'object' && num(v.chapter.n) >= 1
        ? { n: num(v.chapter.n), title: v.chapter.title ?? '', of: num(v.chapter.of) || 5, unlocks: strs(v.chapter.unlocks) }
        : null,
    season: v.season && typeof v.season === 'object' && typeof v.season.id === 'string' ? { id: v.season.id, name: v.season.name ?? '', emoji: v.season.emoji ?? '' } : null,
    notice: v.notice && typeof v.notice === 'object' && typeof v.notice.text === 'string' && v.notice.text ? { rule: String(v.notice.rule ?? ''), text: v.notice.text } : null,
    prefer_games: v.prefer_games === true,
  };
}

/** v3 messages: `kind` defaults to text; challenge views get safe defaults. */
export function normalizeMessage(m: Message): Message {
  if (!m || typeof m !== 'object') return m;
  const c = m.challenge ?? null;
  return {
    ...m,
    kind: m.kind === 'challenge' && c ? 'challenge' : m.kind === 'gift' ? 'gift' : 'text',
    gift: m.kind === 'gift' ? (m.gift ?? null) : null,
    challenge: c ? { ...c, guesses: c.guesses ?? [], strokes: c.strokes ? c.strokes.map((s) => ({ ...s, points: s.points ?? [] })) : c.strokes, resolved_at: c.resolved_at ?? null } : null,
  };
}

/** Fill v2 fields defensively (Go nil slices → null; older servers omit them). */
export function normalizeMe(m: Me): Me {
  if (!m || typeof m !== 'object') return m;
  const f = m.match_filters ?? { genders: [], countries: [] };
  return {
    ...m,
    interests: m.interests ?? [],
    birthdate: m.birthdate ?? null,
    gender: m.gender ?? null,
    country: m.country ?? null,
    show_country: m.show_country ?? true,
    plan: m.plan ?? 'free',
    plus_until: m.plus_until ?? null,
    match_filters: { genders: f.genders ?? [], countries: f.countries ?? [] },
    filters_active: !!m.filters_active,
    settings: { ...(m.settings ?? { availability: 'open', allow_invites: true }), icebreakers: normalizePrefs(m.settings?.icebreakers) },
    vibes: m.vibes ?? {},
    // v5 (SPEC_V5 §2)
    filter_pass_until: m.filter_pass_until ?? null,
    ads_eligible: !!m.ads_eligible,
    rewards_today: { filter_pass: m.rewards_today?.filter_pass ?? 0, friend_requests: m.rewards_today?.friend_requests ?? 0 },
  };
}

/**
 * Go encodes nil maps/slices as null. Fill safe defaults so components can
 * render straight from the view without null checks everywhere.
 */
export function normalizeGame(v: GameView): GameView {
  if (!v || typeof v !== 'object') return v;
  const base = { ...v, players: v.players ?? [], result: v.result ?? null, deadline: v.deadline ?? null };
  const raw = (v.data ?? {}) as unknown as Record<string, unknown>;
  switch (v.type) {
    case 'two_truths': {
      const d = raw as Partial<TwoTruthsData>;
      return {
        ...base,
        type: 'two_truths',
        data: {
          submitted: d.submitted ?? {},
          my_submission: d.my_submission ?? null,
          subject_id: d.subject_id ?? null,
          statements: d.statements ?? null,
          guessed: d.guessed ?? {},
          my_guess: d.my_guess ?? null,
          reveal: d.reveal ? { lie_index: d.reveal.lie_index, guesses: d.reveal.guesses ?? {} } : null,
        },
      };
    }
    case 'same_page': {
      const d = raw as Partial<SamePageData>;
      return {
        ...base,
        type: 'same_page',
        data: {
          prompt: d.prompt ?? { id: '', a: '', b: '', category: '' },
          my_choice: d.my_choice ?? null,
          chosen: d.chosen ?? {},
          reveal: d.reveal ? { choices: d.reveal.choices ?? {}, in_sync: !!d.reveal.in_sync } : null,
          sync_rounds: d.sync_rounds ?? 0,
          history: (d.history ?? []).map((h) => ({ ...h, choices: h.choices ?? {} })),
        },
      };
    }
    case 'doodle': {
      const d = raw as Partial<DoodleData>;
      return {
        ...base,
        type: 'doodle',
        data: {
          drawer_id: d.drawer_id ?? '',
          word: d.word ?? null,
          hint: d.hint ?? '',
          strokes: (d.strokes ?? []).map((s) => ({ ...s, points: s.points ?? [] })),
          guesses: d.guesses ?? [],
          solved_by: d.solved_by ?? [],
          reveal: d.reveal ?? null,
        },
      };
    }
    case 'hot_takes': {
      const d = raw as Partial<HotTakesData>;
      return {
        ...base,
        type: 'hot_takes',
        data: {
          take: d.take ?? { id: '', text: '', category: '' },
          my_side: d.my_side ?? null,
          picked: d.picked ?? {},
          sides: d.sides ?? null,
          switched: d.switched ?? {},
          history: (d.history ?? []).map((h) => ({ ...h, initial: h.initial ?? {}, final: h.final ?? {} })),
        },
      };
    }
    case 'guess_answer': {
      const d = raw as Partial<GuessAnswerData>;
      return {
        ...base,
        type: 'guess_answer',
        data: {
          answerer_id: d.answerer_id ?? '',
          question: d.question ?? { id: '', text: '', options: ['', '', '', ''], category: '' },
          my_pick: d.my_pick ?? null,
          submitted: d.submitted ?? {},
          reveal: d.reveal ? { answer: d.reveal.answer ?? null, guesses: d.reveal.guesses ?? {} } : null,
        },
      };
    }
    case 'charades': {
      const d = raw as Partial<CharadesData>;
      return {
        ...base,
        type: 'charades',
        data: {
          actor_id: d.actor_id ?? '',
          word: d.word ?? null,
          category: d.category ?? '',
          solved: d.solved ?? [],
          skipped: d.skipped ?? [],
          skips_left: d.skips_left ?? 0,
          guesses: d.guesses ?? [],
          reveal: d.reveal ? { words: d.reveal.words ?? [] } : null,
        },
      };
    }
    case 'trivia': {
      const d = raw as Partial<TriviaData>;
      return {
        ...base,
        type: 'trivia',
        data: {
          question: d.question ?? { id: '', text: '', options: ['', '', '', ''], category: '' },
          my_answer: d.my_answer ?? null,
          answered: d.answered ?? {},
          reveal: d.reveal ? { correct: d.reveal.correct, answers: d.reveal.answers ?? {}, fastest: d.reveal.fastest ?? null } : null,
        },
      };
    }
    // v13 games: the server always sends maps and lists; keep the old-null guard anyway.
    case 'same_wave': {
      const d = raw as Partial<SameWaveData>;
      return { ...base, type: 'same_wave', data: { ...(d as SameWaveData), guessed: d.guessed ?? {}, history: d.history ?? [], reveal: d.reveal ?? null, clue: d.clue ?? null, target: d.target ?? null, my_guess: d.my_guess ?? null, team: d.team ?? 0, team_max: d.team_max ?? 0 } };
    }
    case 'npat': {
      const d = raw as Partial<NPATData>;
      return { ...base, type: 'npat', data: { ...(d as NPATData), fields: d.fields ?? ['name', 'place', 'animal', 'thing'], my_answers: d.my_answers ?? {}, filled: d.filled ?? {}, done: d.done ?? {}, reveal: d.reveal ?? null, matches: d.matches ?? 0, history: d.history ?? [] } };
    }
    case 'antakshari': {
      const d = raw as Partial<AntakshariData>;
      return { ...base, type: 'antakshari', data: { ...(d as AntakshariData), title: d.title ?? null, judged: d.judged ?? {}, my_vote: d.my_vote ?? null, last: d.last ?? null, history: d.history ?? [] } };
    }
    case 'bucket_list': {
      const d = raw as Partial<BucketData>;
      const cards = d.cards ?? [];
      return { ...base, type: 'bucket_list', data: { cards, my_swipes: d.my_swipes ?? cards.map(() => null), progress: d.progress ?? {}, reveal: d.reveal ? { matches: d.reveal.matches ?? [], yes: d.reveal.yes ?? {}, swipes: d.reveal.swipes ?? {} } : null } };
    }
    case 'memory_lane': {
      const d = raw as Partial<MemoryLaneData>;
      return { ...base, type: 'memory_lane', data: { ...(d as MemoryLaneData), my_answer: d.my_answer ?? null, answered: d.answered ?? {}, reveal: d.reveal ?? null, right: d.right ?? 0, history: d.history ?? [] } };
    }
    case 'rank_it': {
      const d = raw as Partial<RankItData>;
      return { ...base, type: 'rank_it', data: { ...(d as RankItData), my_order: d.my_order ?? null, ranked: d.ranked ?? {}, reveal: d.reveal ?? null, history: d.history ?? [] } };
    }
    case 'we_would': {
      const d = raw as Partial<WeWouldData>;
      return { ...base, type: 'we_would', data: { ...(d as WeWouldData), picks: d.picks ?? {}, my_pick: d.my_pick ?? null, reveal: d.reveal ?? null, agreed: d.agreed ?? 0, history: d.history ?? [] } };
    }
    case 'story_cards': {
      const d = raw as Partial<StoryCardsData>;
      return { ...base, type: 'story_cards', data: { ...(d as StoryCardsData), openers: d.openers ?? [], chosen: d.chosen ?? null, loved: d.loved ?? [], my_love: !!d.my_love, history: d.history ?? [] } };
    }
    case 'plan_trip': {
      const d = raw as Partial<PlanTripData>;
      const t = d.ticket;
      return {
        ...base,
        type: 'plan_trip',
        data: {
          ...(d as PlanTripData),
          my_pick: d.my_pick ?? null,
          picked: d.picked ?? {},
          reveal: d.reveal ?? null,
          matches: d.matches ?? 0,
          ticket: { where: t?.where ?? null, when: t?.when ?? null, how: t?.how ?? null, stay: t?.stay ?? null, do: t?.do ?? [] },
        },
      };
    }
    case 'jugnu': {
      const d = raw as Partial<JugnuData>;
      return { ...base, type: 'jugnu', data: { course: d.course ?? '', seed: d.seed ?? 0, flying_since: d.flying_since ?? null, fly_ms: d.fly_ms ?? 60_000, submitted: d.submitted ?? {}, my_run: d.my_run ?? null, reveal: d.reveal ?? null, history: d.history ?? [], best: d.best ?? {} } };
    }
    case 'two_keys': {
      const d = raw as Partial<TwoKeysData>;
      return { ...base, type: 'two_keys', data: { ...(d as TwoKeysData), version: d.version === 'b' ? 'b' : 'a', grid: d.grid ?? [], found: d.found ?? [], total: d.total ?? 0, misses: d.misses ?? 0, reveal: d.reveal ?? null, history: d.history ?? [] } };
    }
    default:
      return v;
  }
}
